/**
 * Which client an admin works on in the Training Hub (cdb_tqs#1574).
 *
 * Trainings, surveys, participants and materials live in the owning client's
 * database. cdb_tqs resolves the client before its first query: an employee
 * always works on their own client, an admin on the one named by `client_id`
 * (default: their own). A training of another client is therefore a 404
 * unless the request names that client — for lists AND for every call on a
 * single training, material or participant.
 *
 * The admin picks the client once in the hub, and the training and activities
 * services append it to every call instead of each call site threading it
 * through. cdb_tqs ignores `client_id` from non-admins, so an accidental scope
 * can never widen anyone's access.
 *
 * One pick for all Training windows: the services cannot tell which window a
 * call comes from, so two windows showing different clients would send each
 * other's calls to the wrong database. Every window follows the pick instead.
 * `null` means "no pick" — the admin's own client, which cdb_tqs uses anyway.
 * The last Training window to close clears it, so no later session inherits it.
 */

let scopedClientId: string | null = null
let holders = 0
const listeners = new Set<() => void>()

export function setTrainingClientScope(clientId: string | null): void {
  if (clientId === scopedClientId) return
  scopedClientId = clientId
  listeners.forEach((listener) => listener())
}

export function getTrainingClientScope(): string | null {
  return scopedClientId
}

export function subscribeTrainingClientScope(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Held by every open Training window; returns the release. */
export function holdTrainingClientScope(): () => void {
  holders += 1
  let released = false
  return () => {
    if (released) return
    released = true
    holders -= 1
    if (holders === 0) setTrainingClientScope(null)
  }
}

/**
 * `url` with the scoped client appended. Unchanged when no client is scoped,
 * when the call already names one, and for public links — those carry their
 * client in the token and are used by visitors without an account.
 */
export function withTrainingClientScope(url: string): string {
  if (!scopedClientId) return url
  if (/[?&]client_id=/.test(url) || url.includes('/trainings/public/')) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}client_id=${encodeURIComponent(scopedClientId)}`
}
